import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import { type ThemeConfig, getThemeById, DEFAULT_THEME_ID } from "./themes";
import { api } from "./api";
import { getCalendarTimeZone } from "@shared/calendarTimeZone";

export type FontScale = "small" | "medium" | "large";
const DEFAULT_FONT_SCALE: FontScale = "medium";
const FONT_SCALE_OPTIONS: FontScale[] = ["small", "medium", "large"];

const DEFAULT_DASHBOARD_TITLE = "InControl";
const DEFAULT_OWNER_NAME = "";

interface BrandingConfig {
  dashboardTitle: string;
  ownerName: string;
}

export type TaskDuration = "0" | "15" | "30" | "45" | "60";
const DEFAULT_TASK_DURATION: TaskDuration = "0";

export type DefaultTaskType = "recurring" | "one_time" | "all_day" | "unscheduled";
const DEFAULT_TASK_TYPE: DefaultTaskType = "unscheduled";

export type CalendarView = "day" | "week" | "month" | "sessions" | "tasks" | "list" | "folders";

export interface CalendarSettings {
  defaultView: CalendarView;
  tasksDefaultView: "list" | "folders";
  dayStart: number;
  dayEnd: number;
  weekStart: string;
  defaultTaskDuration: TaskDuration;
  defaultTaskType: DefaultTaskType;
  defaultTaskFolder: string;
  includeCompletedTasks: boolean;
  showPublicAvailability: boolean;
}

const DEFAULT_CALENDAR_SETTINGS: CalendarSettings = {
  defaultView: "month",
  tasksDefaultView: "list",
  dayStart: 8,
  dayEnd: 17,
  weekStart: "Sunday",
  defaultTaskDuration: DEFAULT_TASK_DURATION,
  defaultTaskType: DEFAULT_TASK_TYPE,
  defaultTaskFolder: "",
  includeCompletedTasks: false,
  showPublicAvailability: false,
};

interface ThemeContextValue {
  theme: ThemeConfig;
  setTheme: (themeId: string) => void;
  fontScale: FontScale;
  setFontScale: (scale: FontScale) => void;
  branding: BrandingConfig;
  setBranding: (branding: BrandingConfig) => void;
  defaultTaskDuration: TaskDuration;
  setDefaultTaskDuration: (d: TaskDuration) => void;
  calendarSettings: CalendarSettings;
  /** Read-only workspace authority; never persisted through viewer preferences. */
  calendarTimeZone: string;
  setCalendarSettings: (s: Partial<CalendarSettings>) => void;
  settingsLoaded: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyTheme(theme: ThemeConfig) {
  const root = document.documentElement;

  const colorMap: Record<string, string> = {
    "--background": theme.colors.background,
    "--foreground": theme.colors.foreground,
    "--card": theme.colors.card,
    "--card-foreground": theme.colors.cardForeground,
    "--popover": theme.colors.popover,
    "--popover-foreground": theme.colors.popoverForeground,
    "--primary": theme.colors.primary,
    "--primary-foreground": theme.colors.primaryForeground,
    "--secondary": theme.colors.secondary,
    "--secondary-foreground": theme.colors.secondaryForeground,
    "--muted": theme.colors.muted,
    "--muted-foreground": theme.colors.mutedForeground,
    "--accent": theme.colors.accent,
    "--accent-foreground": theme.colors.accentForeground,
    "--destructive": theme.colors.destructive,
    "--destructive-foreground": theme.colors.destructiveForeground,
    "--border": theme.colors.border,
    "--input": theme.colors.input,
    "--ring": theme.colors.ring,
    "--success": theme.colors.success,
    "--warning": theme.colors.warning,
    "--info": theme.colors.info,
    "--sidebar": theme.colors.sidebar,
    "--sidebar-foreground": theme.colors.sidebarForeground,
    "--sidebar-primary": theme.colors.sidebarPrimary,
    "--sidebar-primary-foreground": theme.colors.sidebarPrimaryForeground,
    "--sidebar-accent": theme.colors.sidebarAccent,
    "--sidebar-accent-foreground": theme.colors.sidebarAccentForeground,
    "--sidebar-border": theme.colors.sidebarBorder,
    "--sidebar-ring": theme.colors.sidebarRing,
  };

  for (const [prop, value] of Object.entries(colorMap)) {
    root.style.setProperty(prop, value);
  }

  root.style.setProperty("--glow-color", theme.effects.glowColor);
  root.style.setProperty("--grid-color", theme.effects.gridColor);
  root.style.setProperty("--glow-enabled", theme.effects.glowEnabled ? "1" : "0");
  root.style.setProperty("--grid-enabled", theme.effects.gridEnabled ? "1" : "0");
}

function applyFontScale(scale: FontScale) {
  const root = document.documentElement;
  FONT_SCALE_OPTIONS.forEach(s => root.classList.remove(`font-scale-${s}`));
  root.classList.add(`font-scale-${scale}`);
}

function persistSettings(data: Record<string, any>) {
  api.settings.update(data).catch(() => {});
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [theme, setThemeState] = useState<ThemeConfig>(() => {
    return getThemeById(DEFAULT_THEME_ID);
  });

  const [fontScale, setFontScaleState] = useState<FontScale>(DEFAULT_FONT_SCALE);
  const [defaultTaskDuration, setDefaultTaskDurationState] = useState<TaskDuration>(DEFAULT_TASK_DURATION);
  const [calendarSettings, setCalendarSettingsState] = useState<CalendarSettings>(DEFAULT_CALENDAR_SETTINGS);
  const [calendarTimeZone, setCalendarTimeZone] = useState(() => getCalendarTimeZone());

  const [branding, setBrandingState] = useState<BrandingConfig>({
    dashboardTitle: DEFAULT_DASHBOARD_TITLE,
    ownerName: DEFAULT_OWNER_NAME,
  });

  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    api.settings.get().then((s) => {
      setCalendarTimeZone(getCalendarTimeZone(s));
      // There is exactly one theme ("default"); any stored legacy preference
      // resolves to it.
      setThemeState(getThemeById(DEFAULT_THEME_ID));

      const fs = FONT_SCALE_OPTIONS.includes(s.fontSize as FontScale) ? (s.fontSize as FontScale) : DEFAULT_FONT_SCALE;
      setFontScaleState(fs);

      setBrandingState({
        dashboardTitle: s.dashboardTitle || DEFAULT_DASHBOARD_TITLE,
        ownerName: s.ownerName ?? DEFAULT_OWNER_NAME,
      });

      const dur = ["0", "15", "30", "45", "60"].includes(s.defaultTaskDuration) ? (s.defaultTaskDuration as TaskDuration) : DEFAULT_TASK_DURATION;
      setDefaultTaskDurationState(dur);

      const taskType = (["recurring", "one_time", "all_day", "unscheduled"].includes(s.defaultTaskType) ? s.defaultTaskType : DEFAULT_TASK_TYPE) as DefaultTaskType;

      setCalendarSettingsState({
         defaultView: (
           s.calendarDefaultView === "list" || s.calendarDefaultView === "folders"
             ? "tasks"
             : ["day", "week", "month", "tasks"].includes(s.calendarDefaultView)
               ? s.calendarDefaultView
               : DEFAULT_CALENDAR_SETTINGS.defaultView
         ) as CalendarView,
        tasksDefaultView: s.tasksDefaultView === "folders" ? "folders" : "list",
        dayStart: typeof s.calendarDayStart === "number" ? s.calendarDayStart : DEFAULT_CALENDAR_SETTINGS.dayStart,
        dayEnd: typeof s.calendarDayEnd === "number" ? s.calendarDayEnd : DEFAULT_CALENDAR_SETTINGS.dayEnd,
        weekStart: s.calendarWeekStart || DEFAULT_CALENDAR_SETTINGS.weekStart,
        defaultTaskDuration: dur,
        defaultTaskType: taskType,
        defaultTaskFolder: typeof s.defaultTaskFolder === "string" ? s.defaultTaskFolder : "",
        includeCompletedTasks: s.includeCompletedTasks === true,
        showPublicAvailability: s.calendarShowPublicAvailability === true,
      });

      setSettingsLoaded(true);
    }).catch(() => {
      setSettingsLoaded(true);
    });
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    applyFontScale(fontScale);
  }, [fontScale]);

  const setTheme = (themeId: string) => {
    const base = getThemeById(themeId);
    setThemeState(base);
    persistSettings({ theme: base.id });
  };

  const setFontScale = (scale: FontScale) => {
    setFontScaleState(scale);
    persistSettings({ fontSize: scale });
  };

  const setBranding = (config: BrandingConfig) => {
    setBrandingState(config);
    persistSettings({ dashboardTitle: config.dashboardTitle, ownerName: config.ownerName });
  };

  const setDefaultTaskDuration = (d: TaskDuration) => {
    setDefaultTaskDurationState(d);
    setCalendarSettingsState(prev => ({ ...prev, defaultTaskDuration: d }));
    persistSettings({ defaultTaskDuration: d });
  };

  const setCalendarSettings = (partial: Partial<CalendarSettings>) => {
    setCalendarSettingsState(prev => {
      const next = { ...prev, ...partial };
      const persistData: Record<string, any> = {};
      if ("defaultView" in partial) persistData.calendarDefaultView = partial.defaultView;
      if ("tasksDefaultView" in partial) {
        persistData.tasksDefaultView = partial.tasksDefaultView;
        // Clear the session localStorage preference AND the toggle-generated URL
        // param so the freshly-saved server default wins on the next page load.
        try { window.localStorage.removeItem("tasks:arrangement"); } catch { /* ignore */ }
        try {
          if (typeof window !== "undefined" && window.location.pathname === "/tasks") {
            const params = new URLSearchParams(window.location.search);
            params.delete("view");
            params.delete("folder");
            const qs = params.toString();
            window.history.replaceState(
              {},
              "",
              window.location.pathname + (qs ? "?" + qs : ""),
            );
          }
        } catch { /* ignore */ }
      }
      if ("dayStart" in partial) persistData.calendarDayStart = partial.dayStart;
      if ("dayEnd" in partial) persistData.calendarDayEnd = partial.dayEnd;
      if ("weekStart" in partial) persistData.calendarWeekStart = partial.weekStart;
      if ("defaultTaskDuration" in partial) {
        persistData.defaultTaskDuration = partial.defaultTaskDuration;
        setDefaultTaskDurationState(partial.defaultTaskDuration!);
      }
      if ("defaultTaskType" in partial) {
        persistData.defaultTaskType = partial.defaultTaskType;
      }
      if ("defaultTaskFolder" in partial) {
        persistData.defaultTaskFolder = partial.defaultTaskFolder;
      }
      if ("includeCompletedTasks" in partial) {
        persistData.includeCompletedTasks = partial.includeCompletedTasks;
      }
      if ("showPublicAvailability" in partial) {
        persistData.calendarShowPublicAvailability = partial.showPublicAvailability;
      }
      persistSettings(persistData);
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, fontScale, setFontScale, branding, setBranding, defaultTaskDuration, setDefaultTaskDuration, calendarSettings, calendarTimeZone, setCalendarSettings, settingsLoaded }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
